/* eslint-disable */
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { useEffect, useState } from 'react'
import ResourceTreeView from './ResourceTreeView'
import { editManagePermission, uploadUserInfo } from '../../apiCalls/permissionApi'
import ErrorSnackAlert from '../../components/PlatformLayout/ErrorSnackAlert'
import { useApiPost } from '../../apiCalls/useApi'
import UserInfo from './UserInfo'
import * as yup from 'yup'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

interface Permission {
  scope: string
  scope_id: string
  scope_name?: string
  role: string
}

interface User {
  id: string
  email: string
  firstname: string
  lastname: string
  phonenumber: string
  permission: Permission[]
}

interface UserPermissionDetailDialogProps {
  user: User
  open: boolean
  setSaveClickUpdate: React.Dispatch<React.SetStateAction<number>>
  onClose: () => void
}

const schema = yup.object().shape({
  firstname: yup.string().required('First name is required'),
  lastname: yup.string().required('Last name is required'),
  email: yup.string().email('Invalid email address').required('Email is required'),
  phonenumber: yup
    .string()
    .notRequired()
    .test('is-valid-phone', 'Phone number must be a valid US or Canadian number', function (value) {
      if (!value) return true
      const phoneRegex = /^(\+?1)?[ -.]?(\(?[2-9]\d{2}\)?)[ -.]?\d{3}[ -.]?\d{4}$/
      return phoneRegex.test(value)
    }),
})

export default function UserPermissionDetailDialog({
  user: initialUser,
  open,
  setSaveClickUpdate,
  onClose,
}: UserPermissionDetailDialogProps) {
  const [currentUser, setCurrentUser] = useState<User>(initialUser)
  const [permissionsObjectToApiRequest, setPermissionsObjectToApiRequest] = useState<any>(null)
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [isErrorAlertOpen, setIsErrorAlertOpen] = useState(false)
  const [resetFlag, setResetFlag] = useState<number>(0)
  const editAdminManagePermission = useApiPost(editManagePermission)
  const editUserInfo = useApiPost(uploadUserInfo)
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      firstname: initialUser.firstname || '',
      lastname: initialUser.lastname || '',
      email: initialUser.email || '',
      phonenumber: initialUser.phonenumber || '',
    },
    resolver: yupResolver(schema),
  })

  useEffect(() => {
    setCurrentUser(initialUser)
    reset({
      firstname: initialUser.firstname || '',
      lastname: initialUser.lastname || '',
      email: initialUser.email || '',
      phonenumber: initialUser.phonenumber || '',
    })
  }, [initialUser, reset])

  const handleCloseErrorAlert = () => {
    setIsErrorAlertOpen(false)
  }

  const saveOnClick = handleSubmit(async (userInfo: any) => {
    if (!permissionsObjectToApiRequest || permissionsObjectToApiRequest.permissions.length === 0) {
      setIsErrorAlertOpen(true)
      return
    }
    const userInfoObjectToApiRequest = { ...userInfo, userId: currentUser.id }
    const uploadUserInfoErr = await editUserInfo(userInfoObjectToApiRequest)
    if (uploadUserInfoErr) return

    const editAdminManagePermissionErr = await editAdminManagePermission(
      permissionsObjectToApiRequest
    )
    if (editAdminManagePermissionErr) return
    const updatedPermissions = permissionsObjectToApiRequest.permissions.map((perm: any) => ({
      scope: perm.scopeType,
      scope_id: perm.scopeID,
      role: perm.role,
    }))

    setCurrentUser((prevUser) => ({
      ...prevUser,
      permission: updatedPermissions,
    }))

    setIsEditing(false)
    setResetFlag((prev) => prev + 1)
    setSaveClickUpdate((prev) => prev + 1)
  })

  const handleCancel = () => {
    setIsEditing(false)
    reset()
    setResetFlag((prev) => prev + 1)
    setPermissionsObjectToApiRequest(null)
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        setIsEditing(false)
        onClose()
        setResetFlag((prev) => prev + 1)
      }}
      PaperProps={{
        style: {
          width: '90vw',
          maxWidth: '90vw',
          maxHeight: '85vh',
          height: '85vh',
          margin: 'auto',
          backgroundColor: '#FFFFFF',
          borderRadius: '10px',
        },
      }}
    >
      {!isEditing && (
        <Button
          variant='contained'
          className='buttonEnable'
          style={{
            position: 'absolute',
            right: '110px',
            top: '30px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => {
            setIsEditing(true)
          }}
        >
          <EditOutlinedIcon sx={{ marginRight: '5px' }} />
          Edit
        </Button>
      )}
      <DialogTitle>User Permission Details</DialogTitle>
      <DialogContent>
        <Grid container spacing={4}>
          <UserInfo isEditing={isEditing} control={control} errors={errors} isUpdate={true} />

          <div
            style={{
              display: 'flex',
              height: '60vh',
              borderLeft: '1px solid black',
              maxWidth: '1px',
              margin: '20px 16px',
            }}
          ></div>

          <Grid item xs={12} md={6}>
            <Typography variant='h6' sx={{ paddingLeft: '30px' }}>
              Manage Permissions
            </Typography>
            <ResourceTreeView
              isEditing={isEditing}
              user={currentUser}
              setPermissionsObjectToApiRequest={setPermissionsObjectToApiRequest}
              resetFlag={resetFlag}
            />
          </Grid>
        </Grid>
        {isEditing && (
          <div
            style={{ marginTop: '30px', display: 'flex', width: '94%', justifyContent: 'flex-end' }}
          >
            <Button
              variant='contained'
              color='primary'
              sx={{ marginRight: '10px' }}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button variant='contained' color='primary' onClick={saveOnClick}>
              Save
            </Button>
          </div>
        )}
      </DialogContent>
      <ErrorSnackAlert
        open={isErrorAlertOpen}
        handleCloseError={handleCloseErrorAlert}
        text='You cannot set an empty permission.'
      />
    </Dialog>
  )
}
